import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TutorialProgress, GettingStartedData } from 'src/types/tutorial';

interface TutorialState {
  currentTour: string | null;
  tourRunning: boolean;
  tourStep: number;
  tutorialProgress: Record<string, TutorialProgress>;
  gettingStartedData: GettingStartedData | null;
  gettingStartedCompleted: boolean;
  welcomeScreenShown: boolean;
}

const initialState: TutorialState = {
  currentTour: null,
  tourRunning: false,
  tourStep: 0,
  tutorialProgress: {},
  gettingStartedData: null,
  gettingStartedCompleted: false,
  welcomeScreenShown: false,
};

// Load from localStorage
const loadTutorialState = (): Partial<TutorialState> => {
  try {
    const saved = localStorage.getItem('telix_tutorial_state');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        tutorialProgress: parsed.tutorialProgress || {},
        gettingStartedData: parsed.gettingStartedData || null,
        gettingStartedCompleted: parsed.gettingStartedCompleted || false,
        welcomeScreenShown: parsed.welcomeScreenShown || false,
      };
    }
  } catch (error) {
    console.error('Failed to load tutorial state:', error);
  }
  return {};
};

const savedState = loadTutorialState();

const tutorialSlice = createSlice({
  name: 'tutorial',
  initialState: { ...initialState, ...savedState },
  reducers: {
    startTour: (state, action: PayloadAction<string>) => {
      state.currentTour = action.payload;
      state.tourRunning = true;
      state.tourStep = 0;
    },
    stopTour: (state) => {
      state.tourRunning = false;
      state.currentTour = null;
      state.tourStep = 0;
    },
    setTourStep: (state, action: PayloadAction<number>) => {
      state.tourStep = action.payload;
    },
    updateTutorialProgress: (state, action: PayloadAction<TutorialProgress>) => {
      state.tutorialProgress[action.payload.tutorialId] = action.payload;
      // Persist to localStorage
      try {
        const toSave = {
          tutorialProgress: state.tutorialProgress,
          gettingStartedData: state.gettingStartedData,
          gettingStartedCompleted: state.gettingStartedCompleted,
          welcomeScreenShown: state.welcomeScreenShown,
        };
        localStorage.setItem('telix_tutorial_state', JSON.stringify(toSave));
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
    setGettingStartedData: (state, action: PayloadAction<GettingStartedData>) => {
      state.gettingStartedData = action.payload;
      // Persist to localStorage
      try {
        const toSave = {
          tutorialProgress: state.tutorialProgress,
          gettingStartedData: state.gettingStartedData,
          gettingStartedCompleted: state.gettingStartedCompleted,
          welcomeScreenShown: state.welcomeScreenShown,
        };
        localStorage.setItem('telix_tutorial_state', JSON.stringify(toSave));
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
    setGettingStartedCompleted: (state, action: PayloadAction<boolean>) => {
      state.gettingStartedCompleted = action.payload;
      // Persist to localStorage
      try {
        const toSave = {
          tutorialProgress: state.tutorialProgress,
          gettingStartedData: state.gettingStartedData,
          gettingStartedCompleted: state.gettingStartedCompleted,
          welcomeScreenShown: state.welcomeScreenShown,
        };
        localStorage.setItem('telix_tutorial_state', JSON.stringify(toSave));
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
    setWelcomeScreenShown: (state, action: PayloadAction<boolean>) => {
      state.welcomeScreenShown = action.payload;
      // Persist to localStorage
      try {
        const toSave = {
          tutorialProgress: state.tutorialProgress,
          gettingStartedData: state.gettingStartedData,
          gettingStartedCompleted: state.gettingStartedCompleted,
          welcomeScreenShown: state.welcomeScreenShown,
        };
        localStorage.setItem('telix_tutorial_state', JSON.stringify(toSave));
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
  },
});

export const {
  startTour,
  stopTour,
  setTourStep,
  updateTutorialProgress,
  setGettingStartedData,
  setGettingStartedCompleted,
  setWelcomeScreenShown,
} = tutorialSlice.actions;

export default tutorialSlice.reducer;

